import React, { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Upload,
  X,
} from 'lucide-react';
import ExpirationBar from '../../components/ExpirationBar';
import SignalStrip from '../../components/SignalStrip';
import SortableHeader from '../../components/SortableHeader';
import StatusBadge from '../../components/StatusBadge';
import { fmtDate, fmtMoney, relativeTime } from '../../utils/format';
import { counterpartyPathForName } from '../counterparties/counterpartyApi';

function Highlight({ search, text }) {
  if (!search || !text) return <>{text || ''}</>;
  const idx = String(text).toLowerCase().indexOf(search.toLowerCase());
  if (idx < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark>{text.slice(idx, idx + search.length)}</mark>
      {text.slice(idx + search.length)}
    </>
  );
}

// Accessible DESCRIPTION for one of the two date sort buttons — not its name.
// Clicking an inactive column sorts it ascending
// (useContractsListData.toggleSort), so that's what the "activate to" half
// promises. Each button's NAME stays the bare visible word; see the dual header
// below for why the state has to ride on the description instead.
function dateSortDescription(field, active, sortOrder) {
  if (!active) return `${field}. Not sorted. Activate to sort ascending.`;
  return sortOrder === 'asc'
    ? `${field}. Sorted ascending. Activate to sort descending.`
    : `${field}. Sorted descending. Activate to sort ascending.`;
}

// Stable, hardcoded ids — aria-describedby is an IDREF, and generating ids at
// render time would hand assistive tech a fresh target on every sort.
const START_SORT_DESC_ID = 'contracts-sort-desc-effective';
const EXPIRES_SORT_DESC_ID = 'contracts-sort-desc-expiration';

export default function ContractsListTable({
  canWriteContracts,
  clearFilters,
  contracts,
  error,
  handlePageSizeChange,
  hasFilters,
  loading,
  onRetry,
  page,
  pageSize,
  pageSizeOptions,
  pagination,
  search,
  selectedIds,
  setPage,
  setTag,
  sortField,
  sortOrder,
  toggleSelectAllOnPage,
  toggleSelected,
  toggleSort,
}) {
  const navigate = useNavigate();

  // Multi-select renders for every user: read-only roles can bulk-download
  // files, and the bulk bar gates its write actions per capability.
  const selectable = !!selectedIds;
  const allOnPageSelected = selectable && contracts.length > 0 &&
    contracts.every((c) => selectedIds.has(c.id));
  const someOnPageSelected = selectable && contracts.some((c) => selectedIds.has(c.id));

  // "Some but not all" renders as indeterminate — only settable via the DOM.
  const headerCheckboxRef = useRef(null);
  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someOnPageSelected && !allOnPageSelected;
    }
  }, [someOnPageSelected, allOnPageSelected]);

  // doc-230: the placeholder is FIRST-LOAD only. Showing it on every `loading`
  // unmounted the table on each sort/filter/page refetch, which destroyed the
  // focused sort button — keyboard focus fell to <body> and a second
  // Space/Enter could not flip the direction the way a second mouse click does.
  // A refetch over rows we already have keeps the table mounted and marks it
  // aria-busy instead.
  const firstLoad = loading && contracts.length === 0;
  const refreshing = loading && contracts.length > 0 && !error;

  return (
    <div className="card">
      {firstLoad ? (
        <div style={{ padding: '80px 20px', textAlign: 'center' }}>
          <div className="muted" style={{ fontSize: 13 }}>Loading…</div>
        </div>
      ) : error ? (
        <div className="callout red" style={{ margin: 16 }}>
          <div className="callout-body">
            <strong>Failed to load contracts.</strong>
            <div style={{ marginTop: 4, fontSize: 12 }}>{error}</div>
            {onRetry && (
              <button className="btn outline sm" style={{ marginTop: 10 }} onClick={onRetry}>
                <RefreshCw className="w-3.5 h-3.5" />
                Retry
              </button>
            )}
          </div>
        </div>
      ) : contracts.length === 0 ? (
        <div style={{ padding: '80px 20px', textAlign: 'center' }}>
          <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
            {hasFilters ? 'No matching contracts.' : 'No contracts yet.'}
          </div>
          {hasFilters ? (
            <button className="btn outline sm" onClick={clearFilters}>
              <X className="w-3 h-3" />
              Clear filters
            </button>
          ) : canWriteContracts ? (
            <Link to="/contracts/upload" className="btn primary sm">
              <Upload className="w-3.5 h-3.5" />
              Upload your first contract
            </Link>
          ) : null}
        </div>
      ) : (
        <div
          style={{ overflowX: 'auto' }}
          className={refreshing ? 'list-refreshing' : undefined}
          aria-busy={refreshing || undefined}
        >
          {/* aria-describedby targets for the two date sort buttons, parked
              OUTSIDE the table on purpose. A <th>'s accessible name is computed
              from its CONTENTS, so anything with text inside that cell — an
              sr-only span included — rolls up into the column header name that
              screen readers re-announce on every cell in the column. Kept out
              here, the header name stays the visible "Start · Expires" while
              each button still announces its own sort state. */}
          <span id={START_SORT_DESC_ID} className="sr-only">
            {dateSortDescription('Effective date', sortField === 'effectiveDate', sortOrder)}
          </span>
          <span id={EXPIRES_SORT_DESC_ID} className="sr-only">
            {dateSortDescription('Expiration date', sortField === 'expirationDate', sortOrder)}
          </span>
          <table className="tbl">
            <thead>
              <tr>
                {selectable && (
                  <th style={{ width: 34 }}>
                    <input
                      type="checkbox"
                      ref={headerCheckboxRef}
                      checked={allOnPageSelected}
                      onChange={toggleSelectAllOnPage}
                      aria-label="Select all contracts on this page"
                      style={{ cursor: 'pointer', display: 'block' }}
                    />
                  </th>
                )}
                <th style={{ width: 36 }} />
                <SortableHeader field="title" sortField={sortField} sortOrder={sortOrder} onToggle={toggleSort} style={{ minWidth: 320 }}>Contract</SortableHeader>
                <SortableHeader field="counterparty" sortField={sortField} sortOrder={sortOrder} onToggle={toggleSort} style={{ width: 220 }}>Counterparty</SortableHeader>
                <SortableHeader field="status" sortField={sortField} sortOrder={sortOrder} onToggle={toggleSort} style={{ width: 80 }}>Status</SortableHeader>
                <SortableHeader field="contractValue" sortField={sortField} sortOrder={sortOrder} onToggle={toggleSort} style={{ width: 100 }} align="right">Value</SortableHeader>
                <th style={{ width: 260 }}>
                  {/* Two sort controls in one cell, so this header can't use
                      SortableHeader. Same rule applies though: real <button>s,
                      not clickable spans, so both are keyboard-operable.
                      Deliberately NO aria-sort here: aria-sort describes ONE
                      column and this cell is two logical columns, so any single
                      value on it is ambiguous ("sorted ascending" — which
                      date?). The sort state rides on each button instead, where
                      it can name its own field — but as a DESCRIPTION, not a
                      name (doc-229): this th's own name is computed from its
                      contents, so per-button labels spelling out the state made
                      the column header name 132 chars and every date cell in
                      every row recited both sentences. Descriptions are not part
                      of the name computation, so each button keeps its bare
                      visible word as its name (WCAG 2.5.3 Label in Name) and
                      points at an sr-only span outside the table. The direction
                      is spelled out in words there, so the caret is
                      aria-hidden. */}
                  <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                    <button
                      type="button"
                      onClick={() => toggleSort('effectiveDate')}
                      className="sortable-inline"
                      aria-label="Start"
                      aria-describedby={START_SORT_DESC_ID}
                    >
                      Start
                      {sortField === 'effectiveDate' && (
                        <span aria-hidden="true" style={{ fontSize: 9, opacity: 0.6, marginLeft: 3 }}>{sortOrder === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </button>
                    <span style={{ opacity: 0.3 }}>·</span>
                    <button
                      type="button"
                      onClick={() => toggleSort('expirationDate')}
                      className="sortable-inline"
                      aria-label="Expires"
                      aria-describedby={EXPIRES_SORT_DESC_ID}
                    >
                      Expires
                      {sortField === 'expirationDate' && (
                        <span aria-hidden="true" style={{ fontSize: 9, opacity: 0.6, marginLeft: 3 }}>{sortOrder === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </button>
                  </span>
                </th>
                <SortableHeader field="updatedAt" sortField={sortField} sortOrder={sortOrder} onToggle={toggleSort} style={{ width: 90 }}>Updated</SortableHeader>
              </tr>
            </thead>
            <tbody>
              {contracts.map((contract) => (
                <tr
                  key={contract.id}
                  onClick={() => navigate(`/contracts/${contract.id}`)}
                  onKeyDown={(event) => {
                    // Only when the row itself is focused — Space on the
                    // selection checkbox must toggle it, not navigate.
                    if (event.target !== event.currentTarget) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      navigate(`/contracts/${contract.id}`);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  style={selectable && selectedIds.has(contract.id)
                    ? { background: 'var(--surface-alpha-primary)' }
                    : undefined}
                >
                  {selectable && (
                    // Checkbox cell must not trigger the row's navigate.
                    <td onClick={(event) => event.stopPropagation()} style={{ cursor: 'default' }}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(contract.id)}
                        onChange={() => toggleSelected(contract.id)}
                        aria-label={`Select ${contract.title}`}
                        style={{ cursor: 'pointer', display: 'block' }}
                      />
                    </td>
                  )}
                  <td><SignalStrip contract={contract} /></td>
                  <td>
                    <div style={{ fontWeight: 450 }}>
                      <Highlight search={search} text={contract.title} />
                    </div>
                    <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                      {contract.shortName && (
                        <>
                          <span className="mono"><Highlight search={search} text={contract.shortName} /></span>
                          <span style={{ margin: '0 5px', opacity: 0.4 }}>·</span>
                        </>
                      )}
                      {contract.contractType || '—'}
                      {contract.needsReview && canWriteContracts && (
                        <>
                          <span style={{ margin: '0 5px', opacity: 0.4 }}>·</span>
                          <span
                            style={{ color: 'var(--text-yellow)' }}
                            title="Auto-created from an upload — needs review"
                          >
                            Needs review
                          </span>
                        </>
                      )}
                      {contract.screeningCreatedVia === 'slack' && (
                        <>
                          <span style={{ margin: '0 5px', opacity: 0.4 }}>·</span>
                          <span title="Created via Slack">via Slack</span>
                        </>
                      )}
                      {contract.internalOwner && <> · {contract.internalOwner}</>}
                    </div>
                    {contract.contentSnippet && (
                      // Best-matching document passage for a content search hit
                      // (doc-101). Plain text with «guillemet» markers — the
                      // snippet comes from untrusted document text.
                      <div
                        className="muted"
                        style={{
                          fontSize: 11,
                          marginTop: 3,
                          maxWidth: 480,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={contract.contentSnippet}
                      >
                        <span style={{ fontStyle: 'italic' }}>in document:</span> {contract.contentSnippet}
                      </div>
                    )}
                    {Array.isArray(contract.tags) && contract.tags.length > 0 && (
                      <div className="row" style={{ gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                        {contract.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="chip gray"
                            style={{ fontSize: 10, cursor: 'pointer' }}
                            onClick={(event) => {
                              event.stopPropagation();
                              setTag(tag);
                            }}
                            title={`Filter to tag "${tag}"`}
                          >
                            {tag}
                          </span>
                        ))}
                        {contract.tags.length > 3 && (
                          <span className="muted" style={{ fontSize: 10 }}>
                            +{contract.tags.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td>
                    {/* The counterparty opens its 360 page (doc-174).
                        stopPropagation because the whole row already navigates
                        to the contract. */}
                    <div className="ellipsis" style={{ fontSize: 13, maxWidth: 200 }}>
                      {contract.counterparty ? (
                        <Link
                          to={counterpartyPathForName(contract.counterparty)}
                          onClick={(event) => event.stopPropagation()}
                          title={`Everything on ${contract.counterparty}`}
                        >
                          <Highlight search={search} text={contract.counterparty} />
                        </Link>
                      ) : '—'}
                    </div>
                    <div
                      className="muted ellipsis"
                      style={{ fontSize: 11, maxWidth: 200 }}
                      title={contract.lightsparkEntity || ''}
                    >
                      {contract.lightsparkEntity || ''}
                    </div>
                  </td>
                  <td><StatusBadge status={contract.status} /></td>
                  <td
                    className="mono"
                    align="right"
                    style={{ color: contract.contractValue ? 'var(--text-primary)' : 'var(--text-tertiary)' }}
                  >
                    {contract.contractValue ? fmtMoney(contract.contractValue, contract.currency) : '—'}
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <ExpirationBar
                        date={contract.expirationDate}
                        effective={contract.effectiveDate}
                        className="wide"
                      />
                      <div
                        className="muted mono"
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: 11,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <span>{contract.effectiveDate ? fmtDate(contract.effectiveDate) : '—'}</span>
                        <span>{contract.expirationDate ? fmtDate(contract.expirationDate) : '—'}</span>
                      </div>
                    </div>
                  </td>
                  <td className="muted" style={{ fontSize: 12 }}>{relativeTime(contract.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Same first-load gate as the table above: the pager must survive a
          refetch too, or paging by keyboard loses focus after every page. */}
      {!firstLoad && !error && contracts.length > 0 && (
        <div
          style={{
            padding: '10px 18px',
            borderTop: '0.5px solid var(--border-primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: 12,
            color: 'var(--text-secondary)',
          }}
        >
          <div className="row" style={{ gap: 12 }}>
            <span>
              Showing <strong style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, pagination.total)}
              </strong> of {pagination.total}
            </span>
            <span className="row" style={{ gap: 6 }}>
              <span>Per page:</span>
              <select
                value={pageSize}
                onChange={(event) => handlePageSizeChange(parseInt(event.target.value, 10))}
                className="filter-select"
                style={{ height: 26, fontSize: 12 }}
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </span>
          </div>
          <div className="row" style={{ gap: 4 }}>
            <button
              className="icon-btn xs"
              onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
              disabled={page === 1}
              aria-label="Previous page"
              style={{ opacity: page === 1 ? 0.4 : 1 }}
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <span className="mono" style={{ padding: '0 8px' }}>
              {page} / {pagination.totalPages || 1}
            </span>
            <button
              className="icon-btn xs"
              onClick={() => setPage((currentPage) => Math.min(pagination.totalPages, currentPage + 1))}
              disabled={page >= pagination.totalPages}
              aria-label="Next page"
              style={{ opacity: page >= pagination.totalPages ? 0.4 : 1 }}
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
